/**
 * ProactiveNotification - SuperJarvis reaching out with helpful nudges
 * 
 * Examples:
 * - "Your 3pm call is in 15 minutes. Need prep materials?"
 * - "Traffic is heavy. Leave 10 minutes early for your meeting."
 * - "You haven't responded to John's email from yesterday."
 * - "Your weekly report is due tomorrow. Start drafting?"
 * 
 * These are generated by Shadow Mode based on learned patterns.
 */
import React, { useState } from 'react';
import { 
  Bell, 
  Clock, 
  AlertTriangle, 
  X,
  ChevronRight,
  Zap
} from './shared';

// =============================================================================
// TYPES
// =============================================================================

type NotificationType = 'reminder' | 'suggestion' | 'alert' | 'insight';
type NotificationPriority = 'low' | 'medium' | 'high';

interface ProactiveNotificationData {
  id: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  icon?: React.ComponentType<{ className?: string }>;
  action?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  timestamp: Date;
  expiresAt?: Date;
  dismissible?: boolean;
}

// =============================================================================
// NOTIFICATION ICON
// =============================================================================

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'reminder':
      return Clock;
    case 'suggestion':
      return Zap;
    case 'alert':
      return AlertTriangle;
    case 'insight':
      return Bell;
    default:
      return Bell;
  }
};

const getNotificationColor = (priority: NotificationPriority) => {
  switch (priority) {
    case 'high':
      return {
        border: 'border-red-400/40',
        bg: 'bg-red-400/10',
        icon: 'text-red-400',
        shadow: 'shadow-[0_0_30px_rgba(239,68,68,0.2)]'
      };
    case 'medium':
      return {
        border: 'border-amber-400/40',
        bg: 'bg-amber-400/10',
        icon: 'text-amber-400',
        shadow: 'shadow-[0_0_30px_rgba(251,191,36,0.2)]'
      };
    default:
      return {
        border: 'border-cyan-400/40',
        bg: 'bg-cyan-400/10',
        icon: 'text-cyan-400',
        shadow: 'shadow-[0_0_30px_rgba(34,211,238,0.2)]'
      };
  }
};

// =============================================================================
// SINGLE NOTIFICATION COMPONENT
// =============================================================================

interface NotificationProps {
  notification: ProactiveNotificationData;
  onDismiss: (id: string) => void;
  onAction?: () => void;
}

export const ProactiveNotification: React.FC<NotificationProps> = ({
  notification,
  onDismiss,
  onAction
}) => {
  const Icon = notification.icon || getNotificationIcon(notification.type);
  const colors = getNotificationColor(notification.priority);

  return (
    <div 
      className={`
        w-full max-w-sm 
        bg-black/90 backdrop-blur-xl 
        border ${colors.border}
        rounded-2xl 
        ${colors.shadow}
        overflow-hidden
        animate-in fade-in slide-in-from-right-4 duration-300
      `}
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-xl ${colors.bg}`}>
            <Icon className={`w-5 h-5 ${colors.icon}`} />
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-white truncate">
              {notification.title}
            </h4>
            <p className="text-sm text-gray-400 mt-1 line-clamp-2">
              {notification.message}
            </p>
          </div>

          {notification.dismissible !== false && (
            <button
              onClick={() => onDismiss(notification.id)}
              className="p-1 hover:bg-white/5 rounded-full transition-colors shrink-0"
            >
              <X className="w-4 h-4 text-gray-500 hover:text-gray-300" />
            </button>
          )}
        </div>

        {/* Actions */}
        {(notification.action || notification.secondaryAction) && (
          <div className="flex gap-2 mt-4">
            {notification.action && (
              <button
                onClick={() => {
                  notification.action?.onClick();
                  onAction?.();
                }}
                className="flex-1 px-4 py-2 bg-cyan-500 hover:bg-cyan-400 text-black text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {notification.action.label}
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
            {notification.secondaryAction && (
              <button
                onClick={notification.secondaryAction.onClick}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 text-gray-300 text-sm rounded-xl transition-colors"
              >
                {notification.secondaryAction.label}
              </button>
            )}
          </div>
        )}

        {/* Timestamp */}
        <div className="text-xs text-gray-600 mt-3">
          {formatTimeAgo(notification.timestamp)}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// NOTIFICATION CENTER
// =============================================================================

interface NotificationCenterProps {
  position?: 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left';
  maxNotifications?: number;
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  position = 'top-right',
  // maxNotifications — will be used when SSE-based notifications arrive
  maxNotifications: _maxNotifications = 3
}) => {
  const [notifications, setNotifications] = useState<ProactiveNotificationData[]>([]);

  // Position classes
  const positionClasses = {
    'top-right': 'fixed top-20 right-4',
    'bottom-right': 'fixed bottom-20 right-4',
    'top-left': 'fixed top-20 left-4',
    'bottom-left': 'fixed bottom-20 left-4'
  }[position];

  // Icon mapping — will be used when SSE-based notifications arrive
  const _iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
    reminder: Clock,
    alert: AlertTriangle,
    suggestion: Zap,
    insight: Bell
  };
  void _maxNotifications; void _iconMap; // Retain for future SSE notification rendering

  // Notifications will arrive via SSE events from the swarm — no polling needed.
  // The /api/notifications endpoint has been removed (410 Gone).
  // Future: notifications push via dynamic_view_ready or a dedicated SSE notification event.

  const handleDismiss = (id: string) => {
    localStorage.setItem(`superjarvis_notification_${id}`, 'true');
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  if (notifications.length === 0) return null;

  return (
    <div className={`${positionClasses} z-50 space-y-3`}>
      {notifications.map((notification) => (
        <ProactiveNotification
          key={notification.id}
          notification={notification}
          onDismiss={handleDismiss}
        />
      ))}
    </div>
  );
};

// =============================================================================
// HELPERS
// =============================================================================

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString();
}

export default ProactiveNotification;
